import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';

const MockInterviewSection = ({ userProgress }) => {
  const [currentInterview, setCurrentInterview] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [interviewType, setInterviewType] = useState('technical');
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [stream, setStream] = useState(null);

  const interviewTypes = [
    {
      id: 'technical',
      name: 'Technical Deep-dive',
      icon: '💻',
      duration: '45-60 min',
      description: 'In-depth technical questions on testing frameworks, automation, and tools',
      color: 'blue'
    },
    {
      id: 'behavioral',
      name: 'Behavioral Assessment',
      icon: '🎭',
      duration: '30-45 min',
      description: 'Situational questions and behavioral assessments for testing roles',
      color: 'green'
    },
    {
      id: 'mixed',
      name: 'Mixed Interview',
      icon: '🔄',
      duration: '60-90 min',
      description: 'Combination of technical and behavioral questions',
      color: 'purple'
    }
  ];

  const sampleQuestions = {
    technical: [
      "Explain the difference between Smoke testing and Sanity testing.",
      "How would you design an automation framework from scratch?",
      "What challenges have you faced with cross-browser testing?",
      "Describe your approach to API testing and what tools you use.",
      "How do you handle flaky tests in your automation suite?"
    ],
    behavioral: [
      "Tell me about a time when you found a critical bug just before release.",
      "How do you prioritize testing when there are tight deadlines?",
      "Describe a situation where you had to work with a difficult developer.",
      "How do you stay updated with the latest testing trends and tools?",
      "Tell me about your biggest testing failure and what you learned."
    ],
    mixed: [
      "Walk me through your testing process for a new feature.",
      "How would you convince management to invest in test automation?",
      "Describe a complex bug you investigated and how you solved it.",
      "What\'s your approach to mentoring junior testers?",
      "How do you balance manual and automated testing?"
    ]
  };

  useEffect(() => {
    let interval = null;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((time) => time + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const startInterview = async (type) => {
    try {
      const mediaStream = await navigator.mediaDevices?.getUserMedia({
        video: true,
        audio: true
      });
      
      setStream(mediaStream);
      if (videoRef?.current) {
        videoRef.current.srcObject = mediaStream;
      }
      
      setCurrentInterview(type);
      setCurrentQuestion(0);
      setRecordingTime(0);
    } catch (error) {
      console.error('Error accessing camera/microphone:', error);
      alert('Unable to access camera/microphone. Please check permissions.');
    }
  };

  const startRecording = () => {
    if (stream) {
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder?.start();
      setIsRecording(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef?.current && isRecording) {
      mediaRecorderRef?.current?.stop();
      setIsRecording(false);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < sampleQuestions?.[interviewType]?.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const endInterview = () => {
    if (stream) {
      stream?.getTracks()?.forEach(track => track?.stop());
      setStream(null);
    }
    setCurrentInterview(null);
    setIsRecording(false);
    setRecordingTime(0);
    setCurrentQuestion(0);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'blue': return 'border-blue-500 bg-blue-50 text-blue-800';
      case 'green': return 'border-green-500 bg-green-50 text-green-800';
      case 'purple': return 'border-purple-500 bg-purple-50 text-purple-800';
      default: return 'border-gray-500 bg-gray-50 text-gray-800';
    }
  };

  if (currentInterview) {
    return (
      <div className="space-y-6">
        {/* Interview Header - Enhanced colors */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {interviewTypes?.find(type => type?.id === interviewType)?.name}
              </h3>
              <p className="text-gray-700 font-medium">
                Question {currentQuestion + 1} of {sampleQuestions?.[interviewType]?.length}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`font-mono text-lg font-bold ${isRecording ? 'text-red-600' : 'text-gray-700'}`}>
                {formatTime(recordingTime)}
              </div>
              <Button variant="outline" onClick={endInterview}>
                End Interview
              </Button>
            </div>
          </div>
        </div>
        {/* Video and Question - Enhanced colors */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Video Preview */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="relative">
              <video
                ref={videoRef}
                autoPlay
                muted
                className="w-full h-64 bg-gray-900 rounded-lg object-cover"
              />
              {isRecording && (
                <div className="absolute top-4 left-4 flex items-center space-x-2 bg-black/70 rounded px-2 py-1">
                  <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" />
                  <span className="text-white text-sm font-semibold">Recording</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-center space-x-4 mt-4">
              {!isRecording ? (
                <Button onClick={startRecording} className="bg-red-600 hover:bg-red-700">
                  Start Recording
                </Button>
              ) : (
                <Button onClick={stopRecording} variant="outline">
                  Stop Recording
                </Button>
              )}
            </div>
          </div>

          {/* Current Question - Enhanced styling */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h4 className="text-lg font-semibold mb-4 text-gray-900">Interview Question</h4>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
              <p className="text-blue-900 text-lg leading-relaxed font-medium">
                {sampleQuestions?.[interviewType]?.[currentQuestion]}
              </p>
            </div>

            <div className="space-y-4">
              <div className="text-sm text-gray-700">
                <p><strong className="text-gray-900">Think time:</strong> Take 30-60 seconds to organize your thoughts</p>
                <p><strong className="text-gray-900">Speaking time:</strong> Aim for 2-4 minutes per answer</p>
                <p><strong className="text-gray-900">Structure:</strong> Use STAR method (Situation, Task, Action, Result) for behavioral questions</p>
              </div>

              <div className="flex space-x-3">
                <Button 
                  onClick={nextQuestion} 
                  disabled={currentQuestion >= sampleQuestions?.[interviewType]?.length - 1}
                  variant="outline"
                >
                  Next Question
                </Button>
                <Button variant="ghost">
                  Repeat Question
                </Button>
              </div>
            </div>
          </div>
        </div>
        {/* AI Feedback Panel - Enhanced colors */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold mb-4 text-gray-900">Real-time AI Feedback</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h5 className="font-semibold text-green-900 mb-2">Communication</h5>
              <div className="text-2xl font-bold text-green-700 mb-1">8.2/10</div>
              <p className="text-sm text-green-800">Clear articulation and pace</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h5 className="font-semibold text-blue-900 mb-2">Technical Depth</h5>
              <div className="text-2xl font-bold text-blue-700 mb-1">7.5/10</div>
              <p className="text-sm text-blue-800">Good technical knowledge</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h5 className="font-semibold text-orange-900 mb-2">Confidence</h5>
              <div className="text-2xl font-bold text-orange-700 mb-1">6.8/10</div>
              <p className="text-sm text-orange-800">Maintain eye contact</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Interview Type Selection - Enhanced colors */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {interviewTypes?.map((type) => (
          <div
            key={type?.id}
            className={`
              p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg
              ${interviewType === type?.id
                ? `${getTypeColor(type?.color)} shadow-md transform scale-105`
                : 'border-gray-300 bg-white hover:border-gray-400 text-gray-900'
              }
            `}
            onClick={() => setInterviewType(type?.id)}
          >
            <div className="text-center">
              <span className="text-4xl mb-3 block">{type?.icon}</span>
              <h3 className="text-lg font-semibold mb-2">{type?.name}</h3>
              <p className="text-sm text-gray-700 mb-3">{type?.description}</p>
              <div className="text-xs text-gray-600 mb-4">Duration: {type?.duration}</div>
              
              {interviewType === type?.id && (
                <Button 
                  onClick={() => startInterview(type?.id)}
                  className="w-full"
                >
                  Start Interview
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Previous Interviews - Enhanced colors */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Previous Mock Interviews</h3>
        
        <div className="space-y-4">
          {[
            { id: 1, type: 'Technical Deep-dive', date: '2025-01-02', score: 8.2, duration: '52 min' },
            { id: 2, type: 'Behavioral Assessment', date: '2024-12-28', score: 7.8, duration: '38 min' },
            { id: 3, type: 'Mixed Interview', date: '2024-12-25', score: 8.5, duration: '67 min' }
          ]?.map((interview) => (
            <div key={interview?.id} className="flex items-center justify-between p-4 border border-gray-300 rounded-lg bg-white">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{interview?.type}</h4>
                <p className="text-sm text-gray-700">{interview?.date} • {interview?.duration}</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="font-bold text-lg text-gray-900">{interview?.score}/10</div>
                  <div className="text-xs text-gray-600">Overall Score</div>
                </div>
                <div className="space-x-2">
                  <Button size="sm" variant="outline">
                    View Recording
                  </Button>
                  <Button size="sm" variant="outline">
                    View Feedback
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Performance Insights - Enhanced text colors */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Interview Performance Insights</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold mb-3 text-green-800">Strengths</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center space-x-2">
                <span className="text-green-600 font-bold">✓</span>
                <span className="text-gray-800">Clear and structured responses</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-600 font-bold">✓</span>
                <span className="text-gray-800">Strong technical knowledge in automation</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-600 font-bold">✓</span>
                <span className="text-gray-800">Good use of real-world examples</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-3 text-orange-800">Areas for Improvement</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center space-x-2">
                <span className="text-orange-600 font-bold">!</span>
                <span className="text-gray-800">Maintain more consistent eye contact</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-orange-600 font-bold">!</span>
                <span className="text-gray-800">Reduce filler words ("um", "uh")</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-orange-600 font-bold">!</span>
                <span className="text-gray-800">Practice concise answers for behavioral questions</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockInterviewSection;